import { useEffect, useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, Stack } from '@mui/material';

import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { BodyText, CaptionText, Subheading } from '../../shared/typography/PrimaryTypography';
import {
  fetchRecord,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import {
  EntityLookupItem,
  MetricLookupItem,
  ProblemDetail,
  UpdateRecordPatch
} from '../../../types/syntiro_honey';
import EntitySelect from './EntitySelect';
import MetricAutocomplete from './MetricAutocomplete';
import PeriodSelect from './PeriodSelect';

interface BulkEditDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The selected record ids the edit will apply to (in selection order). */
  recordIds: Array<string>;
  /** Whether the bulk request is in flight (disables Apply). */
  busy: boolean;
  /** Applies the built patch to every selected record; the parent owns the loop. */
  onApply: (patch: UpdateRecordPatch) => Promise<void>;
  /** Close without applying. */
  onClose: () => void;
}

/**
 * Build the PATCH one selection produces. Only the fields the reviewer actually
 * set are sent — every selected record keeps its other fields untouched, exactly
 * like N individual edits (which is what the parent's loop performs).
 */
function buildPatch(
  metric: MetricLookupItem | null,
  entity: EntityLookupItem | null,
  periodId: string | null
): UpdateRecordPatch {
  const patch: UpdateRecordPatch = {};
  if (metric !== null) {
    patch.metricId = metric.id;
  }
  if (entity !== null) {
    if (entity.kind === 'COMPANY') {
      // Company-level is a marker, not an FK — and the API rejects it beside one.
      patch.companyWide = true;
    } else {
      patch.companyWide = false;
      // Emit every level explicitly, including null for levels the picked entity
      // doesn't reach — an OMITTED key means "leave alone" server-side, so a
      // Division pick would otherwise strand the old subdivisionId/siteId on
      // every selected record instead of clearing them.
      patch.divisionId = entity.divisionId;
      patch.subdivisionId = entity.subdivisionId;
      patch.siteId = entity.siteId;
    }
  }
  if (periodId !== null) {
    patch.periodId = periodId;
  }
  return patch;
}

/**
 * Bulk edit for the current selection (Maxine R5 ②): set a metric, an entity
 * and/or a reporting period ONCE and apply it to every selected record, instead
 * of repeating the same correction row by row. Semantically identical to N manual
 * edits — the same PATCH per record, the same audit events, and a metric change
 * teaches the per-client learned rule for each row's label exactly as a manual
 * remap does.
 * @returns {JSX.Element}
 */
export default function BulkEditDialog({
  open,
  recordIds,
  busy,
  onApply,
  onClose
}: BulkEditDialogProps) {
  const [metric, setMetric] = useState<MetricLookupItem | null>(null);
  const [entity, setEntity] = useState<EntityLookupItem | null>(null);
  const [periodId, setPeriodId] = useState<string | null>(null);
  // The entity/period pickers are company-scoped (a record can only attach to its
  // own client's org tree and calendar). The FIRST selected record supplies that
  // scope; rows of another company fail the server's own reference check and are
  // reported as skipped — visible, never silently mis-assigned.
  const [companyId, setCompanyId] = useState<string | null>(null);
  // Tracks whether the scope lookup is still pending, so the pickers stay
  // disabled from open until a companyId (or a failure) actually lands — a brief
  // false "resolved" gap here is exactly what let a period get picked against no
  // scope at all.
  const [scopeLoading, setScopeLoading] = useState<boolean>(true);
  // A failed lookup must block the pickers, not just leave companyId null: the
  // catalogue-fallback behaviour previously let a reviewer pick an unscoped
  // entity/period that the server then rejects per-row on submit.
  const [scopeProblem, setScopeProblem] = useState<ProblemDetail | null>(null);

  const firstRecordId = recordIds[0] ?? null;
  useEffect(() => {
    if (!open || firstRecordId === null) {
      return undefined;
    }
    let cancelled = false;
    // The scope belongs to THIS selection: drop the previous one up front so a
    // reopened dialog never offers another company's entities/periods while (or
    // if) the lookup is pending/failed.
    setCompanyId(null);
    setScopeProblem(null);
    setScopeLoading(true);
    fetchRecord(firstRecordId)
      .then((record) => {
        if (!cancelled) {
          setCompanyId(record.companyId ?? null);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setScopeProblem(toProblemDetail(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setScopeLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, firstRecordId]);

  // The pickers must stay off until a real companyId is confirmed — while
  // loading, on failure, AND when the record genuinely carries no company (all
  // three would otherwise let a reviewer stage an unscoped change).
  const pickersDisabled = scopeLoading || scopeProblem !== null || companyId === null;

  const patch = buildPatch(metric, entity, periodId);
  const hasChanges = Object.keys(patch).length > 0;

  const resetAndClose = (): void => {
    setMetric(null);
    setEntity(null);
    setPeriodId(null);
    // The dialog stays mounted while closed, so the scope must be reset here or
    // the next opening starts on the previous selection's company.
    setCompanyId(null);
    setScopeProblem(null);
    setScopeLoading(true);
    onClose();
  };

  const handleApply = async (): Promise<void> => {
    await onApply(patch);
    resetAndClose();
  };

  return (
    <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="sm">
      <Subheading weight="Bold" sx={{ m: 2 }}>
        Edit {recordIds.length} selected record{recordIds.length === 1 ? '' : 's'}
      </Subheading>
      <DialogContent>
        <Stack spacing={2}>
          <BodyText>
            Set one or more fields below. Every selected record gets the same change; fields you
            leave empty stay as they are on each record.
          </BodyText>
          <MetricAutocomplete
            label="Metric"
            selectedId={metric?.id ?? null}
            selectedLabel={metric?.name ?? null}
            onSelect={setMetric}
          />
          {metric !== null && (
            <CaptionText>
              Applying teaches rules for this client: each record&rsquo;s label will map to{' '}
              {metric.name} automatically on future uploads.
            </CaptionText>
          )}
          {scopeProblem !== null && (
            <Alert severity="error">
              {scopeProblem.detail ??
                scopeProblem.title ??
                'Could not resolve the company for this selection. Entity and period cannot be edited until this is retried.'}
            </Alert>
          )}
          <EntitySelect
            label="Entity"
            divisionId={entity?.divisionId ?? null}
            subdivisionId={entity?.subdivisionId ?? null}
            siteId={entity?.siteId ?? null}
            currentLabel={entity?.name ?? null}
            onSelect={setEntity}
            companyId={companyId}
            companyWide={entity?.kind === 'COMPANY'}
            disabled={pickersDisabled}
          />
          <PeriodSelect
            label="Reporting period"
            value={periodId}
            onChange={(next) => setPeriodId(next)}
            companyId={companyId}
            disabled={pickersDisabled}
          />
          <CaptionText>
            Records that cannot take a change (already approved, or another client&rsquo;s) are
            skipped and listed afterwards.
          </CaptionText>
          <DialogActions sx={{ px: 0 }}>
            <GhostButton dark onClick={resetAndClose} disabled={busy}>
              Cancel
            </GhostButton>
            <PrimaryButton
              onClick={() => void handleApply()}
              disabled={!hasChanges || busy}
              loading={busy}
            >
              Apply to {recordIds.length} record{recordIds.length === 1 ? '' : 's'}
            </PrimaryButton>
          </DialogActions>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
